import React, {useEffect, useState, useCallback} from 'react';
import {Form, Col} from 'react-bootstrap';
import {useLocation} from 'react-router-dom';
import {useStoreActions, useStoreState} from 'easy-peasy';
import axios, {AxiosResponse} from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {produce} from 'immer';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import {buildAdminQualityAssuranceURL} from '@/utils/urlBuilder';
import QualityAssuranceModal from './QualityAssuranceModal';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';
import {QualityAssuranceDataStoreModel} from '@/views/admin/stores/qualityAssuranceData';
import {useDateChangeListener} from '@/utils/useDateDependentDataRefresh';
import Icon from '@/components/icons/Icon';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import SelectField from '@/components/forms/fields/SelectField';

interface QualityAssuranceDataItem {
    date: string;
    total: number;
    no_lang: number;
    no_anon: number;
    calls_without_duration?: number;
    calls_with_transcription_waiting?: number;
    calls_with_transcription_error?: number;
    calls_with_transcription_inserted?: number;
    calls_without_compression?: number;
    calls_fully_processed?: number;
    chats_without_original_json?: number;
    chats_without_original_formatted_json?: number;
    chats_fully_processed?: number;
    emails_fully_processed?: number;
    feedbacks_fully_processed?: number;
}

interface QualityAssuranceDataResponse {
    qualityAssuranceData: QualityAssuranceDataItem[];
}

interface QualityAssuranceListViewProps {
    event_type: string;
    date_type: string;
}

interface StoreState {
    qualityAssuranceData: QualityAssuranceDataStoreModel;
}

interface StoreActions {
    qualityAssuranceData: {
        loadQualityAssuranceData: (data: QualityAssuranceDataItem[]) => void;
        setLoading: (loading: boolean) => void;
    };
}

interface TableCell {
    getValue: () => any;
    row: {
        original: QualityAssuranceDataItem;
    };
}

interface ColumnInfo {
    accessorKey: string;
    header: string;
    cell?: (cell: TableCell) => React.ReactNode;
}

const qualityAssuranceDataUpdateRequest = (url: string, paramsDict: Record<string, any>): Promise<AxiosResponse<QualityAssuranceDataResponse>> =>
    axios.get(url, {params: paramsDict})
        .catch((error) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        });

const qualityAssuranceDataUpdateRequestDebounced = AwesomeDebouncePromise(qualityAssuranceDataUpdateRequest, 350);

const QualityAssuranceListView: React.FC<QualityAssuranceListViewProps> = React.memo(
    (props) => {
        const location = useLocation();

        // Extract parameters directly from URL path
        // Expected format: /app/admin/quality-assurance/{event_type}/{date_type}
        const pathParts = location.pathname.split('/').filter(Boolean);
        const qualityAssuranceIndex = pathParts.findIndex(part => part === 'quality-assurance');

        let initialEventType = 'call';
        let initialDateType = 'datetime';

        if (qualityAssuranceIndex !== -1 && pathParts.length > qualityAssuranceIndex + 2) {
            initialEventType = pathParts[qualityAssuranceIndex + 1] || 'call';
            initialDateType = pathParts[qualityAssuranceIndex + 2] || 'datetime';
        }

        // Fallback to props if URL extraction fails
        const event_type = initialEventType || props.event_type || 'call';
        const [date_type, setDateType] = useState(initialDateType || props.date_type || 'datetime');


        const {userData, loading: loadingUserData} = useOptimizedConfig();
        const [selectedDate, setSelectedDate] = useState<string>('');
        const [selectedReportType, setSelectedReportType] = useState<string>('');
        const [selectedReportName, setSelectedReportName] = useState<string>('');
        const [selectedEventType, setSelectedEventType] = useState<string>('');

        const [qualityAssuranceModalOpen, setQualityAssuranceModalOpen] = useState<boolean>(false);

        const qualityAssuranceData = useStoreState((state: StoreState) => state.qualityAssuranceData.items);
        const storeQualityAssuranceData = useStoreActions((actions: StoreActions) => actions.qualityAssuranceData.loadQualityAssuranceData);

        const loadingQualityAssuranceData = useStoreState((state: StoreState) => state.qualityAssuranceData.loading);
        const setLoadingQualityAssuranceData = useStoreActions((actions: StoreActions) => actions.qualityAssuranceData.setLoading);

        const fetchData = useCallback(async (): Promise<void> => {
            setLoadingQualityAssuranceData(true);

            const paramsDict: Record<string, any> = {};

            const response = await qualityAssuranceDataUpdateRequestDebounced(
                (window as any).URLS.qualityAssuranceResource.replace(':event_type', event_type).replace(':date_type', date_type), paramsDict,
            );

            storeQualityAssuranceData(response.data.qualityAssuranceData);

            setLoadingQualityAssuranceData(false);
        }, [event_type, date_type, storeQualityAssuranceData, setLoadingQualityAssuranceData]);

        useEffect(() => {
            fetchData();
        }, [fetchData]);

        // Listen for date changes and refresh data
        useDateChangeListener(fetchData);

        const openQualityAssuranceModal = (reportType: string, reportName: string, eventType: string, date: string): void => {
            setSelectedReportType(reportType);
            setSelectedReportName(reportName);
            setSelectedEventType(eventType);
            setSelectedDate(date);
            setQualityAssuranceModalOpen(true);
        };

        const closeQualityAssuranceModal = (): void => {
            setQualityAssuranceModalOpen(false);
            setSelectedReportType('');
            setSelectedReportName('');
            setSelectedEventType('');
            setSelectedDate('');
        };

        let columnInfo: ColumnInfo[] = [
            {
                accessorKey: 'date',
                header: i18n.t('th_date.text'),
            },
            {
                accessorKey: 'total',
                header: i18n.t('th_total.text'),
                cell: (cell: TableCell) => {
                    if (cell.getValue() === 0) {
                        return (
                            <>{cell.getValue()}</>
                        );
                    } else {
                        return (
                            <button
                                type="button"
                                className="btn btn-link p-0 ml-2"
                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                onClick={() => openQualityAssuranceModal('total', 'total', event_type, cell.row.original.date)}
                            >
                                {`${cell.getValue()} `}
                                <Icon name="open-in-new" size="xs" />
                            </button>
                        );
                    }
                },
            },
            {
                accessorKey: 'no_lang',
                header: i18n.t('th_missing_language.text'),
                cell: (cell: TableCell) => {
                    if (cell.getValue() === 0) {
                        return (
                            <>{cell.getValue()}</>
                        );
                    } else {
                        return (
                            <button
                                type="button"
                                className="btn btn-link p-0 ml-2"
                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                onClick={() => openQualityAssuranceModal('no_lang', 'missing language', event_type, cell.row.original.date)}
                            >
                                {`${cell.getValue()} `}
                                <Icon name="open-in-new" size="xs" />
                            </button>
                        );
                    }
                },
            },
        ];

        if (event_type === 'call') {
            columnInfo = produce(columnInfo, (draft) => {
                draft.push({
                    accessorKey: 'calls_without_duration',
                    header: i18n.t('th_missing_duration.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('calls_without_duration', 'missing duration', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
                draft.push({
                    accessorKey: 'calls_with_transcription_waiting',
                    header: i18n.t('th_waiting_transcription.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('calls_with_transcription_waiting', 'waiting transcription', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
                draft.push({
                    accessorKey: 'calls_with_transcription_error',
                    header: i18n.t('th_transcription_error.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('calls_with_transcription_error', 'transcription error', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
                draft.push({
                    accessorKey: 'calls_with_transcription_inserted',
                    header: i18n.t('th_transcription_inserted.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('calls_with_transcription_inserted', 'transcription inserted', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
                draft.push({
                    accessorKey: 'calls_without_compression',
                    header: i18n.t('th_missing_compression.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('calls_without_compression', 'missing compression', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
            });
        } else if (event_type === 'chat') {
            columnInfo = produce(columnInfo, (draft) => {
                draft.push({
                    accessorKey: 'chats_without_original_json',
                    header: i18n.t('th_missing_original.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('chats_without_original_json', 'missing original payload', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
                draft.push({
                    accessorKey: 'chats_without_original_formatted_json',
                    header: i18n.t('th_missing_formatted.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('chats_without_original_formatted_json', 'missing formatted payload', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
            });
        }

        columnInfo = produce(columnInfo, (draft) => {
            draft.push({
                accessorKey: 'no_anon',
                header: i18n.t('th_missing_anonymisation.text'),
                cell: (cell: TableCell) => {
                    if (cell.getValue() === 0) {
                        return (
                            <>{cell.getValue()}</>
                        );
                    } else {
                        return (
                            <button
                                type="button"
                                className="btn btn-link p-0 ml-2"
                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                onClick={() => openQualityAssuranceModal('no_anon', 'missing anonymisation', event_type, cell.row.original.date)}
                            >
                                {`${cell.getValue()} `}
                                <Icon name="open-in-new" size="xs" />
                            </button>
                        );
                    }
                },
            });
        });

        if (event_type === 'call') {
            columnInfo = produce(columnInfo, (draft) => {
                draft.push({
                    accessorKey: 'calls_fully_processed',
                    header: i18n.t('th_fully_processed.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('calls_fully_processed', 'fully processed', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
            });
        } else if (event_type === 'chat') {
            columnInfo = produce(columnInfo, (draft) => {
                draft.push({
                    accessorKey: 'chats_fully_processed',
                    header: i18n.t('th_fully_processed.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('chats_fully_processed', 'fully processed', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
            });
        } else if (event_type === 'email') {
            columnInfo = produce(columnInfo, (draft) => {
                draft.push({
                    accessorKey: 'emails_fully_processed',
                    header: i18n.t('th_fully_processed.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('emails_fully_processed', 'fully processed', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
            });
        } else if (event_type === 'feedback') {
            columnInfo = produce(columnInfo, (draft) => {
                draft.push({
                    accessorKey: 'feedbacks_fully_processed',
                    header: i18n.t('th_fully_processed.text'),
                    cell: (cell: TableCell) => {
                        if (cell.getValue() === 0) {
                            return (
                                <>{cell.getValue()}</>
                            );
                        } else {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    onClick={() => openQualityAssuranceModal('feedbacks_fully_processed', 'fully processed', event_type, cell.row.original.date)}
                                >
                                    {`${cell.getValue()} `}
                                    <Icon name="open-in-new" size="xs" />
                                </button>
                            );
                        }
                    },
                });
            });
        }

        const userCanAccessQualityAssuranceData = hasPermission('fs.quality_assurance.read', userData?.permissions, true);
        if (!userCanAccessQualityAssuranceData && !loadingUserData) {
            return (
                <>
                    <HelpTextBlock helpText={i18n.t('ht_quality_assurance.text')} />

                    <div className="alert alert-danger" role="alert">
                        {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                    </div>
                </>
            );
        }

        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_quality_assurance.text')} />

                <div className="mb-2 d-flex align-items-center form-row">
                    <Col xs="auto">
                        <Form.Label className="mr-2 mb-3">{i18n.t('jt_sort_by.text')}</Form.Label>
                    </Col>
                    <Col xs="auto">
                        <SelectField
                            name="date_type"
                            value={{
                                value: date_type,
                                label: date_type === 'datetime' ? i18n.t('jt_date_time.text') : i18n.t('jt_created_on.text')
                            }}
                            onChange={(selectedOption) => {
                                const newDateType = selectedOption?.value || 'datetime';
                                setDateType(newDateType);

                                // Update URL without page reload
                                const newUrl = buildAdminQualityAssuranceURL(event_type, newDateType);
                                window.history.pushState({}, '', newUrl);
                            }}
                            options={[
                                {value: 'datetime', label: i18n.t('jt_date_time.text')},
                                {value: 'created_on', label: i18n.t('jt_created_on.text')}
                            ]}
                            customStyles={{
                                control: (provided: any) => ({
                                    ...provided,
                                    minWidth: '150px'
                                })
                            }}
                            isSearchable={false}
                        />
                    </Col>
                </div>

                <LoadingWrapper
                    loading={loadingQualityAssuranceData}
                    loadingText={i18n.t('jt_loading_data_general.text')}
                    minHeight="400px"
                >
                    <div className="card">
                        <div className="card-body">
                                <TableComponent data={qualityAssuranceData} columns={columnInfo} />
                        </div>
                    </div>
                </LoadingWrapper>

                {
                    qualityAssuranceModalOpen ? (
                        <QualityAssuranceModal
                            modalOpen={qualityAssuranceModalOpen}
                            closeModal={closeQualityAssuranceModal}
                            reportType={selectedReportType}
                            reportName={selectedReportName}
                            eventType={selectedEventType}
                            date={selectedDate}
                            dateType={date_type}
                        />
                    ) : null
                }
            </>
        );
    },
);

QualityAssuranceListView.displayName = 'QualityAssuranceListView';

export default QualityAssuranceListView; 